<script setup lang="ts">
import { api } from '@/api';
import {
  AppPermissionsEnum,
  instanceOfUnicheckDeviceDto,
  instanceOfUnicheckSimulatorDto,
  QaEquipmentRtSystemDto,
} from '@/api/generator';
import PtwAddButton from '@/components/buttons/PtwAddButton.vue';
import { TAB_PREFIX } from '@/components/confirmationModals/useFormConfirmationModal.ts';
import ContextMenu from '@/components/ContextMenu/ContextMenu.vue';
import { ContextMenuAction } from '@/components/ContextMenu/types';
import {
  getDefaultContextMenuActions,
  globalContextMenuHeaderItemDefinition,
} from '@/components/ContextMenu/utils';
import DeviceCalibrationState from '@/components/deviceStatus/DeviceCalibrationState.vue';
import DeviceErrorInline from '@/components/deviceStatus/DeviceErrorInline.vue';
import DeviceLicenseInline from '@/components/deviceStatus/DeviceLicenseInline.vue';
import DeviceState from '@/components/deviceStatus/DeviceState.vue';
import EntityChange from '@/components/EntityHistory/EntityChange.vue';
import FirstEntityFeedback from '@/components/PageFeedback/FirstEntityFeedback.vue';
import CommentCount from '@/components/forms/CommentCount/CommentCount.vue';
import { mainNavigationQaEquipment } from '@/components/icons/icons';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import BaseImage from '@/components/images/BaseImage.vue';
import Notification from '@/components/notifications/Notification.vue';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import OverviewTableHeader from '@/components/OverviewTable/OverviewTableHeader.vue';
import { OverviewTableProps, SortItem } from '@/components/OverviewTable/types.ts';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { routenames } from '@/router/routenames';
import { useLicenseStore } from '@/stores/licenseStore.ts';
import { useNotificationStore } from '@/stores/notificationStore.ts';
import { useQaEquipmentStore } from '@/stores/qaEquipmentStore.ts';
import {
  localizedEnumCompare,
  cascadedCompareFunction,
  alphabeticalStringCompare,
} from '@/utils/compareAndSort';
import { isIOTDevice } from '@/utils/rtSystem';
import type { I18nKey } from '@/utils/typings';
import { useLoading } from '@/utils/useLoading';
import QaEquipmentRegistration from '@/views/qaEquipment/components/QaEquipmentRegistration.vue';
import {
  getManufacturerLabel,
  getModelLabel,
  getPatchedQuery,
  isRegistrablePtwQaEquipment,
  successfullyRegisteredMsg,
  useDeleteQaEquipmentWithConfirmation,
} from '@/views/qaEquipment/utils';
import { useTranslation } from 'i18next-vue';
import { computed, onBeforeMount, provide, ref, useTemplateRef, watchEffect } from 'vue';
import { useRouter } from 'vue-router';
import QaEquipmentOverviewFilter from './components/QaEquipmentOverviewFilter.vue';
import { QaEquipmentAddFilterObject, reloadEquipmentKey } from './types';
import { EntityInfo } from '@/components/confirmationModals/types';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults';
import { getEntityChangeEvent } from '@/components/EntityHistory/entityChange';
import { useDisplay } from 'vuetify';
import { useRemainingViewportHeight } from '@/utils/useRemainingVueport';
import { useTableScrollElement } from '@/utils/vuetifyMenuHelper';
import { createOverviewImageProps } from '@/components/images/presets';

const { t } = useTranslation();

const notificationStore = useNotificationStore();
const qaEquipmentStore = useQaEquipmentStore();

const qaEquipment = ref<QaEquipmentRtSystemDto[]>();
const constQaEquipment = ref<QaEquipmentRtSystemDto[]>();
const filterObject = ref<QaEquipmentAddFilterObject>({});

const router = useRouter();
const overviewImageProps = createOverviewImageProps({
  fallbackIcon: mainNavigationQaEquipment,
  cover: true,
});

const licenseStore = useLicenseStore();

const tableRef = useTemplateRef<HTMLDivElement>('table');
const { height } = useRemainingViewportHeight(tableRef);
const tableScrollElement = useTableScrollElement(tableRef);

const { mdAndDown } = useDisplay();

qaEquipmentStore.registrationSuccess = () => {
  if (qaEquipmentStore.deviceForRegistration) {
    notificationStore.success(successfullyRegisteredMsg(qaEquipmentStore.deviceForRegistration));
    qaEquipmentStore.setDeviceForRegistration(undefined);
  }
};

const sortBy = ref<SortItem[]>([
  {
    key: 'name',
    order: 'asc',
  },
]);

const {
  action: loadEquipment,
  loading,
  loadingFirstTime,
} = useLoading(async () => {
  const filterQuery = getPatchedQuery(sortBy.value, filterObject.value);
  const { result } = await api.qaEquipmentApi.getQaEquipmentRtSystemList(filterQuery);
  if (result?.value) {
    qaEquipment.value = result.value;
  }
  if (constQaEquipment.value === undefined && qaEquipment.value?.length) {
    constQaEquipment.value = [...qaEquipment.value];
  }
}, true);

const subModelAvailable = computed(() =>
  qaEquipment.value?.some((qaEquipment) => !!qaEquipment?.subModel),
);

function getManufacturerAndModel(qaEquipment: QaEquipmentRtSystemDto) {
  return `${t(getManufacturerLabel(qaEquipment) as I18nKey)} | ${getModelLabel(qaEquipment)}`;
}

const { action: deleteAction } = useDeleteQaEquipmentWithConfirmation();

const headers = computed<OverviewTableProps<QaEquipmentRtSystemDto, string>['headers']>(() => [
  {
    title: '',
    key: 'image',
    sortable: false,
    width: overviewImageProps.desktopWidth,
  },
  {
    title: ``,
    key: 'name',
    sortable: true,
  },
  {
    title: t('Description'),
    key: 'description',
    width: '20%',
  },
  {
    title: t('StateOfUse'),
    key: 'state',
    sort: localizedEnumCompare('StateOfUseEnum'),
  },
  {
    title: t('Type_one'),
    key: 'subCategory',
    sort: localizedEnumCompare('MedicalSystemSubCategoryEnum'),
  },
  {
    title: `${t('Manufacturer')} | ${t('Model')}`,
    key: 'manufacturer',
    sortRaw: cascadedCompareFunction<QaEquipmentRtSystemDto>([
      (a, b) => localizedEnumCompare('RtSystemManufacturerEnum')(a.manufacturer, b.manufacturer),
      (a, b) => alphabeticalStringCompare(getModelLabel(a), getModelLabel(b)),
    ]),
  },
  {
    title: t('LastModificationTime'),
    key: 'lastModificationTime',
  },
  globalContextMenuHeaderItemDefinition,
]);

function getContextMenuActions(item: QaEquipmentRtSystemDto): ContextMenuAction[] {
  const defaultContextMenuActions = getDefaultContextMenuActions(item);
  return [
    {
      ...defaultContextMenuActions.view,
      to: {
        name: routenames.qaEquipment.details,
        params: { id: item.id },
      },
    },
    {
      ...defaultContextMenuActions.edit,
      to: {
        name: routenames.qaEquipment.edit,
        params: { id: item.id },
      },
    },
    {
      ...defaultContextMenuActions.delete,
      callback: async () => {
        const { result } = await deleteAction({
          param: item.id,
          confirmationOptions: getDefaultDeleteConfirmationOptions(
            {
              ...(item as EntityInfo),
              typeName: 'DosimetryAndQaEquipmentEntityLowerCased_one',
            },
            t,
          ),
        });
        if (result?.success) {
          constQaEquipment.value = undefined;
          await loadEquipment();
        }
      },
    },
  ];
}

function navigateTo(equipmentId: string, hash?: string) {
  router.push({
    name: routenames.qaEquipment.details,
    hash,
    params: { id: equipmentId },
  });
}

watchEffect(() => {
  loadEquipment();
});

// Register after change
// Allow the Dialog to reload the equipment after the de-/registration
const reloadEquipment = () => {
  loadEquipment();
};

provide(reloadEquipmentKey, reloadEquipment);

const showFirstEntityFeedback = computed(
  () =>
    !loading.value &&
    qaEquipment.value?.length === 0 &&
    Object.keys(filterObject.value).length === 0,
);

onBeforeMount(async () => {
  await licenseStore.fetchLicenseData();
});
</script>

<template>
  <BasePage :loading="loadingFirstTime">
    <template #title>
      <BasePageTitle :title="$t('DosimetryAndQaEquipmentEntity_other')" />
    </template>

    <template #actions>
      <PtwAddButton
        v-if="!showFirstEntityFeedback"
        :to="{ name: routenames.qaEquipment.add.configuration }"
      />
    </template>
    <template v-if="constQaEquipment?.length">
      <Notification
        v-for="item in qaEquipment?.filter(
          (qa) =>
            (instanceOfUnicheckDeviceDto(qa) || instanceOfUnicheckSimulatorDto(qa)) &&
            qa.calibrationState === 'Outdated',
        )"
        :key="item.id"
        :value="{
          type: 'warning',
        }"
        class="my-4"
      >
        <span v-if="isRegistrablePtwQaEquipment(item)"
          >{{
            $t('ANewCalibrationCertificateIsAvailableFor', {
              name: item.name,
              iuid: item.iuid,
              serialNumber: item.serialNumber,
            })
          }}
          <router-link
            class="ml-2 text-warning text-decoration-underline"
            :to="{
              name: routenames.qaEquipment.edit,
              params: { id: item.id },
              hash: `#${TAB_PREFIX}calibration`,
            }"
            >{{ $t('ActivateCertificate') }}</router-link
          >
        </span>
      </Notification>

      <QaEquipmentOverviewFilter v-model="filterObject" :items="constQaEquipment" />

      <OverviewTable
        ref="table"
        v-model:sortBy="sortBy"
        class="mt-2"
        :headers="headers"
        itemLabel="DosimetryAndQaEquipmentEntity"
        data-testid="DataTable_DosimetryAndQaEquipmentEntity"
        :items="qaEquipment"
        :loading="loading"
        :height="height"
        fixedHeader
        @click:row="
          (_event, data) => {
            navigateTo(data.item.id);
          }
        "
      >
        <template #header.manufacturer="headerProps">
          <OverviewTableHeader v-bind="headerProps">
            <span
              >{{ $t('Manufacturer') }} | {{ $t('Model') }}
              <template v-if="subModelAvailable">
                <span> | {{ $t('SubModel') }}</span>
              </template>
            </span>
          </OverviewTableHeader>
        </template>

        <template #header.name="headerProps">
          <OverviewTableHeader v-bind="headerProps">
            {{ $t('Name') }}
            <span class="font-weight-medium">| {{ $t('ID') }}</span>
          </OverviewTableHeader>
        </template>

        <template #item.image="{ item }">
          <BaseImage v-bind="overviewImageProps" :pictureId="item.imageId" />
        </template>

        <template #item.name="{ item }">
          <div class="d-flex flex-column flex-wrap align-items-start justify-content-start">
            <IdentifierLabel :identifier="item.iuid" :label="item.name" />
            <CommentCount
              :value="item"
              @click.stop="navigateTo(item.id, `#${TAB_PREFIX}comments`)"
            />
            <DeviceCalibrationState v-if="isIOTDevice(item)" :deviceId="item.id" />
            <DeviceErrorInline v-if="isIOTDevice(item)" :device="item" :withDetailsLink="false" />
            <DeviceLicenseInline :device="item" :variant="mdAndDown ? 'icon-only' : 'default'" />
          </div>
        </template>

        <template #item.state="{ item }">
          <div v-if="isIOTDevice(item) && item.state === 'Enabled'" class="d-flex flex-column">
            <DeviceState :deviceId="item.id" orientation="horizontal" />
          </div>
          <div v-else>
            {{ $t(`StateOfUseEnum.${item.state}` as I18nKey) }}
            <EntityChange
              :entityChange="getEntityChangeEvent(item, 'editing')"
              :showEmptyFieldSign="false"
            />
          </div>
        </template>

        <template #item.subCategory="{ item }">
          {{ $t(`QaEquipmentSubCategoryEnum.${item.subCategory}` as I18nKey) }}
        </template>

        <template #item.manufacturer="{ item }">
          {{ getManufacturerAndModel(item) }}
        </template>

        <template #item.lastModificationTime="{ item }">
          <EntityChange :entityChange="getEntityChangeEvent(item, 'modified')" />
        </template>

        <template #item.contextMenu="{ item }">
          <ContextMenu
            :actions="getContextMenuActions(item)"
            :closeOnScrollElements="tableScrollElement"
          />
        </template>
      </OverviewTable>

      <QaEquipmentRegistration immediate />
    </template>
    <FirstEntityFeedback
      v-if="showFirstEntityFeedback"
      :permission="AppPermissionsEnum.LocalDosimetryAndQaEquipmentAdd"
      :noPermissionMessage="$t('NoQaEquipmentConfigured')"
      :to="{ name: routenames.qaEquipment.add.configuration }"
      :actionText="$t('AddDosimetryQaEntity')"
      :icon="mainNavigationQaEquipment"
    />
  </BasePage>
</template>

<style lang="scss" scoped>
.device-state {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  :deep(.device-battery-state .icon-wrapper) {
    background-color: transparent !important;
  }

  :deep(.device-connection-state .icon-wrapper) {
    background-color: transparent !important;
  }
}
</style>
